import React, { Component } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import IconPlus from '../IconPlus/IconPlus';
import { SEARCH_PAGE_TYPE } from '../../../util/marketplace';
import { ARCHIVED_TABS, LISTING_STATUS, SELL_AS_IS_TABS } from '../../../constants/listing';
import { IconArrowHead } from '../../../components';

import css from './SelectSingleFilterPlain.module.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

class SelectSingleFilterPlain extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: this.props.isOpenInitial ?? true,
    };
    this.wrapperRef = React.createRef();
    this.selectOption = this.selectOption.bind(this);
    this.toggleIsOpen = this.toggleIsOpen.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  // Only for tableView in 2ndLife marketplace: collapse filters when user clicks outside of the tabel filter section
  handleClickOutside(event) {
    if (
      this.props.is2ndLife &&
      this.props.isTableView &&
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target)
    ) {
      this.setState({ isOpen: false });
    }
  }

  selectOption(option, subFilter, e) {
    const { queryParamNames, onSelect, onOptionSelect, filterKey } = this.props;

    const queryParamName = getQueryParamName(queryParamNames);

    if (onOptionSelect) {
      onOptionSelect(option, subFilter, filterKey);
    }

    if (option === 'archived') {
      onSelect({
        archived: true,
        [queryParamName]: undefined,
        meta_isBuyerConfirmPending: undefined,
        meta_buyerCompanyId: undefined,
        meta_hasContactedLeasingUser: undefined,
        meta_leaseExpirationOption: undefined,
        meta_contractEndOwnerCompanyName: undefined,
        forSale: undefined,
        asIs: undefined,
        pendingIntakeState: undefined,
        sellAsIsState: undefined,
        meta_shipped: undefined,
        tab: ARCHIVED_TABS.BOUGHT,
        owned: undefined,
        pub_contractEndDateTimestamp: undefined,
      });
    } else if (option === 'sellAsIs') {
      onSelect({
        [queryParamName]: option,
        forSale: undefined,
        archived: undefined,
        meta_isBuyerConfirmPending: undefined,
        meta_buyerCompanyId: undefined,
        meta_hasContactedLeasingUser: undefined,
        meta_leaseExpirationOption: undefined,
        meta_contractEndOwnerCompanyName: undefined,
        sellAsIsState: undefined,
        asIs: undefined,
        pendingIntakeState: undefined,
        pub_contractEndDateTimestamp: undefined,
        tab: undefined,
        owned: undefined,
      });
    } else if (option === 'selling') {
      onSelect({
        [queryParamName]: 'sellAsIs',
        forSale: undefined,
        archived: undefined,
        meta_isBuyerConfirmPending: undefined,
        meta_buyerCompanyId: undefined,
        meta_hasContactedLeasingUser: undefined,
        meta_leaseExpirationOption: undefined,
        meta_contractEndOwnerCompanyName: undefined,
        sellAsIsState: 'selling',
        asIs: true,
        pendingIntakeState: undefined,
        pub_contractEndDateTimestamp: undefined,
        tab: SELL_AS_IS_TABS.FOR_SALE,
        owned: undefined,
      });
    } else if (option === 'published') {
      onSelect({
        [queryParamName]: option,
        forSale: true,
        archived: undefined,
        meta_isBuyerConfirmPending: undefined,
        meta_buyerCompanyId: undefined,
        meta_hasContactedLeasingUser: undefined,
        meta_leaseExpirationOption: undefined,
        meta_contractEndOwnerCompanyName: undefined,
        asIs: undefined,
        pendingIntakeState: undefined,
        pub_contractEndDateTimestamp: undefined,
        sellAsIsState: undefined,
        meta_shipped: undefined,
        tab: LISTING_STATUS.PUBLISHED,
        owned: undefined,
      });
    } else if (filterKey === 'status') {
      onSelect({
        [queryParamName]: option,
        pendingIntakeState: option === 'pendingIntake' ? subFilter : undefined,
        archived: undefined,
        meta_isBuyerConfirmPending: undefined,
        meta_buyerCompanyId: undefined,
        meta_hasContactedLeasingUser: undefined,
        meta_leaseExpirationOption: undefined,
        meta_contractEndOwnerCompanyName: undefined,
        forSale: undefined,
        asIs: undefined,
        sellAsIsState: undefined,
        meta_shipped: undefined,
        tab: undefined,
        owned: undefined,
        pub_contractEndDateTimestamp: undefined,
      });
    } else {
      onSelect({
        [queryParamName]: option,
      });
    }

    if (this.props.isTableView) {
      this.setState({ isOpen: !this.state.isOpen });
    }

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  toggleIsOpen() {
    this.setState({ isOpen: !this.state.isOpen });
  }

  render() {
    const {
      rootClassName,
      className,
      label,
      options,
      queryParamNames,
      initialValues,
      twoColumns,
      useBorder,
      useHighlight,
      intl,
      location,
      searchPageType,
    } = this.props;

    const isMyListingsPage = searchPageType
      ? searchPageType === SEARCH_PAGE_TYPE.MY_LISTINGS
      : location?.pathname.includes('my-listings') ||
        location?.pathname === '/' ||
        location.pathname === '/en/' ||
        location.pathname === '/no/';

    // display 'archived' and 'pending sold' options only if it's the 'my-listings' page
    const filteredOptions =
      label === 'General.leaseStatus'
        ? options.filter(option => {
            if (option.key === 'archived' || option.key === 'pendingSold') {
              return isMyListingsPage;
            }
            return true;
          })
        : options;

    const hasOptions = filteredOptions.length > 0;

    const queryParamName = getQueryParamName(queryParamNames);

    const initialValue =
      queryParamName === 'meta_status'
        ? location?.search.includes('archived')
          ? 'archived'
          : location?.search.includes('forSale=true')
          ? 'published'
          : location?.search.includes('sellAsIsState=selling')
          ? 'selling'
          : initialValues && initialValues[queryParamName]
        : initialValues && initialValues[queryParamName]
        ? initialValues[queryParamName]
        : null;

    const labelClass = initialValue ? css.labelSelected : css.label;

    const optionsContainerClass = classNames({
      [css.optionsContainerOpen]: this.state.isOpen,
      [css.optionsContainerClosed]: !this.state.isOpen,
      [css.twoColumns]: twoColumns,
    });

    let classes = classNames(css[rootClassName], css.root, className);

    return (
      <div className={classes} ref={this.wrapperRef}>
        <div className={css.filterHeader}>
          <button className={css.labelButton} onClick={this.toggleIsOpen}>
            <span className={css.labelButtonContent}>
              <span className={labelClass}>
                {/* listing models are dynamic, so we don't have translations for them */}
                {this.props.isTableView && initialValue
                  ? this.props.filterKey === 'dimrModel'
                    ? initialValue
                    : intl.formatMessage({ id: `General.${initialValue}.label` })
                  : intl.formatMessage({ id: label })}
              </span>
              <span className={css.openSign}>
                {this.props.isTableView ? (
                  <IconArrowHead direction={this.state.isOpen ? 'up' : 'down'} />
                ) : (
                  <IconPlus isOpen={this.state.isOpen} isSelected={!!initialValue} />
                )}
              </span>
            </span>
          </button>
        </div>
        <div className={optionsContainerClass}>
          <div>
            {hasOptions ? (
              filteredOptions.map(option => {
                // check if this option is selected
                const selected = initialValue === option.key;

                const optionClass = classNames(css.option, {
                  [css.optionHighlight]: selected && useHighlight,
                });

                const optionBorderClass = useBorder
                  ? classNames({
                      [css.optionBorderSelected]: selected,
                      [css.optionBorder]: !selected,
                    })
                  : null;

                const initialSubFilterValue =
                  initialValues && initialValues['pendingIntakeState']
                    ? initialValues['pendingIntakeState']
                    : initialValues && initialValues['sellAsIsState']
                    ? initialValues['sellAsIsState']
                    : null;

                return (
                  <span key={option.key}>
                    <button
                      className={classNames(optionClass, css.mainOption)}
                      onClick={() => this.selectOption(option.key, undefined)}
                    >
                      {useBorder ? <span className={optionBorderClass} /> : null}
                      {option.isTranslatedLabel
                        ? option.label
                        : intl.formatMessage({ id: option.label })}
                      {option?.count !== undefined && (
                        <span className={css.count}>({option.count})</span>
                      )}
                    </button>

                    {option?.subFilters && (
                      <span className={css.subFilters}>
                        {option?.subFilters.map(subFilter => (
                          <button
                            className={classNames(css.option, {
                              [css.optionHighlight]: initialSubFilterValue === subFilter.value,
                            })}
                            key={subFilter.value}
                            onClick={() => this.selectOption(option.key, subFilter.value)}
                          >
                            {intl.formatMessage({ id: subFilter.label })}
                            {subFilter?.count !== undefined && (
                              <span className={css.count}>({subFilter.count})</span>
                            )}
                          </button>
                        ))}
                      </span>
                    )}
                  </span>
                );
              })
            ) : (
              <div className={css.noOptionsMessage}>No options match the current filters.</div>
            )}
          </div>

          {hasOptions && (
            <button className={css.clearButton} onClick={e => this.selectOption(null, null, e)}>
              <FormattedMessage id={'SelectSingleFilter.plainClear'} />
            </button>
          )}
        </div>
      </div>
    );
  }
}

SelectSingleFilterPlain.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  twoColumns: false,
  useHighlight: true,
  useBorder: false,
};

SelectSingleFilterPlain.propTypes = {
  rootClassName: string,
  className: string,
  queryParamNames: arrayOf(string).isRequired,
  label: node.isRequired,
  onSelect: func.isRequired,

  options: arrayOf(
    shape({
      key: string.isRequired,
      label: string.isRequired,
    })
  ).isRequired,
  initialValues: object,
  twoColumns: bool,
  useHighlight: bool,
  useBorder: bool,
};

export default SelectSingleFilterPlain;
